import { computed, makeAutoObservable } from 'mobx';

import { getLocations, updateLocation, addLocation, deleteLocation } from '../services/LocationsAPI';
import { Location, LocationModel } from '../models/Location';
import { ReferenceData } from '../models/common';
import { errorStore } from './ErrorStore';
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class LocationStore {
  data: Location[] = [];
  isLoading = false;
  showModal = false;
  currentRow: LocationModel | undefined

  constructor() {
    makeAutoObservable(this);
  }

  async loadData() {
    if (this.data.length || this.isLoading) return;
    const departmentCode = getActiveDepartmentCode();

    try {
      this.setLoading(true);
      const response = await getLocations();
      if (departmentCode !== getActiveDepartmentCode()) {
        return;
      }

      this.setData(response || []);
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async reloadForActiveDepartment() {
    this.reset();
    await this.loadData();
  }

  async delete(location: Location) {
    try {
      this.setLoading(true);
      await deleteLocation(location.id)
      this.setData(this.data.filter(a => a.id !== location.id));
      this.clearForm();
    } catch (error: any) {
      this.setError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async update(location: Location) {
    try {
      this.setLoading(true);
      await updateLocation(location);
      const index = this.data.findIndex(a => a.id === location.id);
      this.data[index] = { ...location, name: location.name };
    } catch (error: any) {
      this.setError(error.message);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async addNew(newlocation: Location, onSuccess?: () => void) {
    this.setLoading(true);
    try {
      const addedNewlocation = await addLocation(newlocation);
      if (addedNewlocation) {
        this.addRow(addedNewlocation);
        this.clearForm();
        onSuccess?.();
      }
    } catch (error: any) {
      this.setError(error.message)
    } finally {
      this.setLoading(false);
    }
  }

  setupNew(name?: string) {
    this.currentRow = new LocationModel({ id: '', name: name || '', isActive: true });
    this.setShowModal(true);
  }

  @computed
  get options() {
    return this.data as ReferenceData[];
  };

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setError(error: string) {
    errorStore.report(error);
  }

  addRow(location: Location) {
    this.data = [location, ...this.data];
  }

  setShowModal(state: boolean) {
    this.showModal = state;
  }

  setCurrent(currentLocation: LocationModel) {
    this.currentRow = currentLocation;
  }

  removeCurrent() {
    this.currentRow = undefined;
  }

  clearForm() {
    this.removeCurrent();
    this.setShowModal(false);
  }

  reset() {
    this.data = [];
    this.isLoading = false;
    this.clearForm();
  }

  setData(data: Location[]) {
    this.data = data;
  }
}

export const locationStore = new LocationStore();
